import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailMode, setEmailMode] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="login-page">
      <div className="overline mb-3">Welcome back</div>
      <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight mb-8">Log in to YUMO</h1>
      <Card className="p-7 rounded-2xl border-border shadow-none">
        <GoogleAuthButton label="Continue with Google" testId="login-google-btn" />
        <div className="flex items-center gap-3 my-5" data-testid="auth-divider">
          <div className="flex-1 h-px bg-border" />
          <span className="overline">OR</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        {!emailMode ? (
          <Button
            type="button"
            variant="ghost"
            className="w-full rounded-full h-11"
            onClick={() => setEmailMode(true)}
            data-testid="login-show-email"
          >
            Continue with Email
          </Button>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
            </div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <Button type="submit" className="w-full rounded-full cta-saffron" disabled={loading} data-testid="login-submit-button">
              {loading ? "Signing in…" : "Log in"}
            </Button>
            <div className="text-xs text-center">
              <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground underline underline-offset-4" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </div>
          </form>
        )}
      </Card>
      <p className="text-sm text-muted-foreground mt-6">
        No account?{" "}
        <Link to="/signup" className="text-foreground underline underline-offset-4" data-testid="goto-signup-link">Create one</Link>
      </p>
    </div>
  );
}
